import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getCampaigns, createCampaign, updateCampaign, deleteCampaign, getProject,
} from '../services/api';
import { useToast } from '../App';
import { useAuth } from '../contexts/AuthContext';
import Modal, { ConfirmModal } from '../components/Modal';
import { HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlinePlay } from 'react-icons/hi';
import { FiTarget, FiCheckCircle, FiXCircle, FiClock, FiMinusCircle, FiAlertTriangle } from 'react-icons/fi';

const STATUT_OPTIONS = ['Planifiee', 'En cours', 'Terminee', 'Annulee'];
const ENV_OPTIONS = ['DEV', 'RECETTE', 'UAT', 'PREPROD', 'PROD'];

const STATUT_STYLE = {
  'Planifiee': { color: 'var(--gray-500)', bg: 'var(--gray-100)', icon: FiClock },
  'En cours': { color: 'var(--info)', bg: 'var(--info-light)', icon: HiOutlinePlay },
  'Terminee': { color: 'var(--green-600)', bg: 'var(--green-100)', icon: FiCheckCircle },
  'Annulee': { color: 'var(--danger)', bg: 'var(--danger-light)', icon: FiXCircle },
};

function CampaignStatusBadge({ statut }) {
  const s = STATUT_STYLE[statut] || STATUT_STYLE['Planifiee'];
  const Icon = s.icon;
  return (
    <span className="badge" style={{ background: s.bg, color: s.color, fontWeight: 500, fontSize: '0.78rem', padding: '0.3em 0.7em', borderRadius: '6px' }}>
      <Icon style={{ marginRight: 4, fontSize: '0.85em' }} />{statut}
    </span>
  );
}

function ProgressBar({ ok, ko, ec, bl, nt, total }) {
  if (total === 0) return <div className="text-muted" style={{ fontSize: '0.78rem' }}>Aucun cas de test</div>;
  const pct = (v) => (v / total * 100).toFixed(1);
  return (
    <div>
      <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--gray-200)', marginBottom: 4 }}>
        {ok > 0 && <div style={{ width: `${pct(ok)}%`, background: 'var(--green-500)' }} title={`OK: ${ok}`} />}
        {ko > 0 && <div style={{ width: `${pct(ko)}%`, background: 'var(--danger)' }} title={`KO: ${ko}`} />}
        {ec > 0 && <div style={{ width: `${pct(ec)}%`, background: 'var(--info)' }} title={`EC: ${ec}`} />}
        {bl > 0 && <div style={{ width: `${pct(bl)}%`, background: 'var(--warning)' }} title={`BL: ${bl}`} />}
        {nt > 0 && <div style={{ width: `${pct(nt)}%`, background: 'var(--gray-300)' }} title={`NT: ${nt}`} />}
      </div>
      <div style={{ display: 'flex', gap: 10, fontSize: '0.72rem', color: 'var(--gray-500)' }}>
        <span><FiCheckCircle style={{ color: 'var(--green-600)' }} /> {ok}</span>
        <span><FiXCircle style={{ color: 'var(--danger)' }} /> {ko}</span>
        <span><FiClock style={{ color: 'var(--info)' }} /> {ec}</span>
        <span><FiAlertTriangle style={{ color: 'var(--warning)' }} /> {bl}</span>
        <span><FiMinusCircle style={{ color: 'var(--gray-400)' }} /> {nt}</span>
        <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{((ok + ko) / total * 100).toFixed(0)}% execute</span>
      </div>
    </div>
  );
}

const emptyForm = { name: '', description: '', version: '', environment: 'DEV' };

export default function CampaignsPage() {
  const { id: projectId } = useParams();
  const addToast = useToast();
  const { isAdmin } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editCampaign, setEditCampaign] = useState(null);
  const [editForm, setEditForm] = useState({ ...emptyForm, statut: 'Planifiee' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [campRes, projRes] = await Promise.all([
        getCampaigns(projectId),
        getProject(projectId),
      ]);
      setCampaigns(campRes.data);
      setProject(projRes.data);
    } catch { /* interceptor */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createCampaign(projectId, form);
      setShowCreate(false);
      setForm({ ...emptyForm });
      load();
      addToast('success', 'Campagne creee avec succes.', 'Campagne creee');
    } catch { addToast('error', 'Erreur lors de la creation.'); }
  };

  const handleOpenEdit = (e, c) => {
    e.stopPropagation();
    setEditCampaign(c);
    setEditForm({ name: c.name, description: c.description, version: c.version, environment: c.environment, statut: c.statut });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateCampaign(projectId, editCampaign.id, editForm);
      setEditCampaign(null);
      load();
      addToast('success', 'Campagne modifiee.', 'Modifiee');
    } catch { addToast('error', 'Erreur lors de la modification.'); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteCampaign(projectId, confirmDelete);
      setConfirmDelete(null);
      load();
      addToast('success', 'Campagne supprimee.', 'Supprimee');
    } catch { addToast('error', 'Erreur lors de la suppression.'); }
  };

  if (loading) return (
    <div className="loading-spinner"><output className="spinner-border"></output><span>Chargement...</span></div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><FiTarget className="me-2" style={{ color: 'var(--primary)' }} />Campagnes de Test</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            {project?.name} — Planifiez et suivez vos campagnes d'execution
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          <HiOutlinePlus /> Nouvelle Campagne
        </button>
      </div>
      {/* Stats globales */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="stat-card">
            <div className="stat-icon stat-icon-navy"><FiTarget /></div>
            <div className="stat-content">
              <div className="stat-number">{campaigns.length}</div>
              <div className="stat-label">Campagnes</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--info-light)', color: 'var(--info)' }}><HiOutlinePlay /></div>
            <div className="stat-content">
              <div className="stat-number">{campaigns.filter(c => c.statut === 'En cours').length}</div>
              <div className="stat-label">En cours</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="stat-card">
            <div className="stat-icon stat-icon-green"><FiCheckCircle /></div>
            <div className="stat-content">
              <div className="stat-number">{campaigns.filter(c => c.statut === 'Terminee').length}</div>
              <div className="stat-label">Terminees</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--danger-light)', color: 'var(--danger)' }}><FiXCircle /></div>
            <div className="stat-content">
              <div className="stat-number">{campaigns.reduce((s, c) => s + c.ko, 0)}</div>
              <div className="stat-label">Total KO</div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des campagnes */}
      {campaigns.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FiTarget /></div>
          <p>Aucune campagne. Creez une campagne pour regrouper et executer vos cas de test.</p>
          <button className="btn btn-primary btn-sm mt-3" onClick={() => setShowCreate(true)}>
            <HiOutlinePlus /> Nouvelle Campagne
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {campaigns.map(c => (
            <div key={c.id} className="col-md-6">
              <div className="card project-card h-100" style={{ cursor: 'pointer', position: 'relative' }}>
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <div>
                      <h5 className="card-title mb-1"><Link to={`/projects/${projectId}/campaigns/${c.id}`} className="text-decoration-none text-dark stretched-link">{c.name}</Link></h5>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <CampaignStatusBadge statut={c.statut} />
                        {c.version && <span className="badge" style={{ background: 'var(--navy-600)', color: '#fff', fontSize: '0.72rem' }}>{c.version}</span>}
                        <span className="badge" style={{ background: 'var(--gray-100)', color: 'var(--gray-600)', fontSize: '0.72rem' }}>{c.environment}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--navy-500)' }}>
                      {c.progress.toFixed(0)}%
                    </div>
                  </div>
                  {c.description && <p className="text-muted small mb-2">{c.description}</p>}
                  <ProgressBar ok={c.ok} ko={c.ko} ec={c.ec} bl={c.bl} nt={c.nt} total={c.total} />
                  <div className="d-flex align-items-center justify-content-between mt-2" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    <span>Par {c.created_by_username || '—'}</span>
                    <span>{c.total} cas de test</span>
                  </div>
                </div>
                <div className="card-footer d-flex gap-2" style={{ position: 'relative', zIndex: 1 }}>
                  <Link to={`/projects/${projectId}/campaigns/${c.id}`} className="btn btn-outline-primary btn-sm">
                    <HiOutlinePlay /> Ouvrir
                  </Link>
                  <button className="btn btn-outline-secondary btn-sm" onClick={(e) => handleOpenEdit(e, c)}>
                    <HiOutlinePencil />
                  </button>
                  <button className="btn btn-outline-danger btn-sm ms-auto" onClick={(e) => { e.stopPropagation(); setConfirmDelete(c.id); }}>
                    <HiOutlineTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Modal creation */}
      <Modal show={showCreate} onClose={() => setShowCreate(false)} title="Nouvelle Campagne" icon={<HiOutlinePlus />}>
        <form onSubmit={handleCreate}>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-campaign-name">Nom de la campagne</label>
            <input id="field-campaign-name" className="form-control" placeholder="Ex: Sprint 12 - Regression" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required autoFocus maxLength={200} />
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label" htmlFor="field-campaign-version">Version / Release</label>
              <input id="field-campaign-version" className="form-control" placeholder="Ex: v2.4.0" value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} maxLength={50} />
            </div>
            <div className="col-6">
              <label className="form-label" htmlFor="field-campaign-environment">Environnement</label>
              <select id="field-campaign-environment" className="form-select" value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })}>
                {ENV_OPTIONS.map(env => <option key={env} value={env}>{env}</option>)}
              </select>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-campaign-description">Description</label>
            <textarea id="field-campaign-description" className="form-control" rows="2" placeholder="Objectif de la campagne" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowCreate(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary btn-sm">Creer</button>
          </div>
        </form>
      </Modal>

      {/* Modal edition */}
      <Modal show={!!editCampaign} onClose={() => setEditCampaign(null)} title="Modifier la campagne" icon={<HiOutlinePencil />}>
        <form onSubmit={handleUpdate}>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-edit-campaign-name">Nom</label>
            <input id="field-edit-campaign-name" className="form-control" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} required maxLength={200} />
          </div>
          <div className="row mb-3">
            <div className="col-4">
              <label className="form-label" htmlFor="field-edit-campaign-version">Version</label>
              <input id="field-edit-campaign-version" className="form-control" value={editForm.version} onChange={e => setEditForm({ ...editForm, version: e.target.value })} maxLength={50} />
            </div>
            <div className="col-4">
              <label className="form-label" htmlFor="field-edit-campaign-environment">Environnement</label>
              <select id="field-edit-campaign-environment" className="form-select" value={editForm.environment} onChange={e => setEditForm({ ...editForm, environment: e.target.value })}>
                {ENV_OPTIONS.map(env => <option key={env} value={env}>{env}</option>)}
              </select>
            </div>
            <div className="col-4">
              <label className="form-label" htmlFor="field-edit-campaign-statut">Statut</label>
              <select id="field-edit-campaign-statut" className="form-select" value={editForm.statut} onChange={e => setEditForm({ ...editForm, statut: e.target.value })}>
                {STATUT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-edit-campaign-description">Description</label>
            <textarea id="field-edit-campaign-description" className="form-control" rows="2" value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditCampaign(null)}>Annuler</button>
            <button type="submit" className="btn btn-primary btn-sm">Enregistrer</button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        show={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer la campagne"
        message="Etes-vous sur de vouloir supprimer cette campagne ? Les resultats d'execution seront perdus."
        confirmText="Supprimer"
      />
    </div>
  );
}
